/* prettier-ignore */

import { Component, computed, inject, ViewEncapsulation } from "@angular/core";
import { ToolsService } from "./services/tools.service";
import { Router, RouterOutlet } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NavigationService } from "./services/navigation.service";
import { RestService } from "./services/rest.service";
import { AuthService } from "./services/auth.service";
import { ModalController } from "@ionic/angular";
import { UserService } from "./services/user.service";
import { StatisticsService } from "./services/statistics.service";
import { GoogleTranslateService } from "./services/google-translate.service";
import { fadeInAnimation } from './route-animations';
import { Location } from '@angular/common';
import { CookieService } from "ngx-cookie-service";
import { DebugService } from "./services/debug.service";
import { ChatBotScriptService } from "./services/chatbot.service";
import { TranslationService } from "./services/translation.service";

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [ fadeInAnimation ] 
})

export class AppComponent {
  public isRegisterMode = computed(() => this.tools.isRegisterMode())
  public isAppLoaded = computed(() => this.tools.isAppLoaded());
  public isTechMode = computed(() => this.tools.isTechMode());
  private isInfoRoutePage = computed(() => this.tools.isInfoRoutePage());
  private debugService = inject(DebugService);
  private _translationService = inject(TranslationService);
  queryParamLang: any;
  chatId: any;

  constructor(
    public modalCtrl: ModalController,
    public translate: TranslateService,
    private router: Router,
    public tools: ToolsService,
    public rest: RestService,
    public navigation: NavigationService,
    public authService: AuthService,
    public userService: UserService,
    public statsService: StatisticsService,
    public googleTranslateService: GoogleTranslateService,
    private cookieService: CookieService,
    private chatService: ChatBotScriptService,
    private location: Location
  ) {
      this.rest.subDomain = 'madrid' //this.tools.getSubdomain(window.location.href); // TODO: EMT HARDCODE
      this.debugService.info(this.rest.subDomain, '[AppComponent] SUBDOMAIN');
      this.checkPreProduction();
      this.navigation.startSaveHistory();
      this.translate.setDefaultLang("es");
      this.translate.use("es");
      this.tools.setCorporiveColors();
  }

  ngOnInit(): void {
    const queryParams = window.location.search ? window.location.search : "";
    const params = new URLSearchParams(queryParams);
    const loadAppFromParam = this.getQueryParam(params);
    this.initializeParams(loadAppFromParam);
    this.checkTechMode();
    this.checkFromDistributor();
    this.checkToken();

    if (this.rest.subDomain) {
      this.rest.getCommonApp(this.statsService.token).subscribe((response) => {
        this.debugService.debug(response, '[AppComponent] Common API Response');

        if(this.isRegisterMode()){
          this.setRegisterMode(response);
          return;
        }

        if (this.router.url.includes("survey-id")) 
          return;

        if (response.error) {
          this.router.navigateByUrl("not-found");
          return;
        }

        this.rest.setDevice(response);
        this.debugService.debug(this.rest.device, '[AppComponent] Device Info');
        this.setLang();

        if(this.checkInfoRoute(response))
          return;

        if (response?.type === 'app'){
          this.userService.checkAllUserEntries();
          this.tools.setIsApp(true);
          this.tools.setAppStarted()
        }
          
        if (response?.type === 'distributor'){
          this.tools.setIsDistributor(true);
          this.tools.setAppStarted()
        }
  
        if (!this.router.url.includes('tech-edit')){
          this.enableChat(response)
        }

      })
      return;
    } 
    this.router.navigateByUrl("not-found");
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  increaseClick() {
    if (!this.tools.showHeader()) 
      return;

    this.statsService.sumClicks();
    this.statsService.setLocalStorageSessionIDFromClick();
    this.statsService.setLocalStorageTokenFromClick();
  }

  getQueryParam(queryParam: URLSearchParams) {
    let params = {};

    if (queryParam.get("lang")) 
      params["lang"] = queryParam.get("lang");

    if (queryParam.get("distributor")) 
      params["distributor"] = queryParam.get("distributor");
    
    if (queryParam.get("token")) 
      params["token"] = queryParam.get("token");
    
    if (queryParam.get("id")) 
      params["id"] = queryParam.get("id");
    
    if (queryParam.get("techMode")) 
      params["techMode"] = queryParam.get("techMode");
    
    if (queryParam.get("showDebug")) 
      params["showDebug"] = queryParam.get("showDebug");
    
    return Object.keys(params).length > 0 ? params : "";
  }

  initializeParams(params) {
    this.debugService.info(params, '[AppComponent] Params');
    this.tools.homeDistributor = null;

    if (params["showDebug"]) {
      this.debugService.enableLogs();
      this.location.replaceState('');
    }

    if (params['techMode']){
      this.debugService.info('Enabling Tech Mode', '[AppComponent]');
      this.tools.isTechMode.set(true);
      this.tools.setWithExpiry('techMode', true, this.rest.ttlTwoHours); //2horas
      this.location.replaceState('');
    }

    if (params["distributor"]) {
      this.tools.homeDistributor =  this.tools.getDistributorUrl(params["distributor"]);
      this.tools.setWithExpiry('homeDistributor', this.tools.homeDistributor, this.rest.ttlTwoHours); //2horas
      this.location.replaceState('');
    };

    if (params["token"]) {
      this.statsService.token = params["token"];
      this.debugService.warn('Setting new token', '[AppComponent]');
      this.tools.setWithExpiry('token', params['token'], this.rest.ttlTwoHours); //2horas
      this.location.replaceState('');
    }

    if (params["lang"]) {
      this.queryParamLang = params["lang"];
      this.location.replaceState('');
    }

    this.statsService.setSessionId();
    if (!params["token"]) 
      this.statsService.setToken();
  }

  checkPreProduction(){
    const preProduction =  this.cookieService.get('preProduction');
    if (!preProduction) 
      return;

    this.rest.subDomain = preProduction.trim();
    this.rest.apiUrlBase = 'https://pre.ciceroneweb.com';
    this.tools.isPreProduction.set(true);
  }

  checkTechMode(){
    const isTechMode = this.tools.getWithExpiry('techMode');
    if (!isTechMode)
      return;

    this.authService.logout();
    this.userService.resetAll();
    this.tools.isTechMode.set(true);
  }

  checkFromDistributor(){
    const fromDistributor = this.tools.getWithExpiry('homeDistributor');
    if (fromDistributor)
      this.tools.homeDistributor = fromDistributor
  }

  checkToken(){
    this.debugService.info('Checking token', '[AppComponent]');
    if (this.statsService.token)
      return;

    const token = this.tools.getWithExpiry("token");
    if (token) {
      this.debugService.info('Restoring Found Token', '[AppComponent]');
      this.statsService.token = token;
      this.tools.setWithExpiry("token", token, this.rest.ttlTwoHours);
    } 
    else 
      this.debugService.warn('Token NOT Found', '[AppComponent]');
  }

  checkInfoRoute(response){
    if (this.isInfoRoutePage()){
      if (response?.type !== 'app' || response?.type !==  'distributor')
        return false;

      if (response?.type === 'app')
        this.tools.setIsApp(true);

      if (response?.type === 'distributor')
        this.tools.setIsDistributor(true);

      this.tools.setCorporativeColors();
      this.tools.enableHeader();
      return true;
    }
  }

  setLang(){
    this.tools.headerLangType.set(this.rest?.device?.headerLangType ?? 'flags')
    const apiLangs = this.rest?.device?.languages;
    const cookieLang = this.googleTranslateService.getLocalLang();
    const mainLang = cookieLang ?? this.tools.getBrowserLang();
    this.tools.setLanguages(apiLangs);
    this.googleTranslateService.changeLangOnInit(mainLang, apiLangs);
    if (apiLangs?.includes(cookieLang)){
      this._translationService.setTranslateService(this.googleTranslateService.currentLang);
      this._translationService.selectedLanguage.set(this.googleTranslateService.currentLang);
    }

    // const mainLang = this.rest.device.mainLanguage ? this.rest.device.mainLanguage : "es";
    // const cookieLang = this.googleTranslateService.getCookieLang();
    // if (cookieLang){
    //   this.tools.changeLanguage(mainLang)
    //   this.googleTranslateService.currentLang = cookieLang;
    // }
    // else 
    //   this.googleTranslateService.changeLanguage(this.queryParamLang || this.googleTranslateService.getLocalLang() || mainLang);
  }

  setRegisterMode(response){
    if (response.error)
      return;

    this.rest.setDevice(response);
    this.tools.setCorporativeColors();
    this.tools.setLanguages(this.rest?.device?.languages)
    this.tools.enableHeader();
    this.enableChat(response)
  }

  enableChat(response){
    this.rest.device.chatBotId = response?.lang?.chatbot;
    this.rest.device.chatBotStandalone = response?.newVersion === 1;
    const mainLang = this.rest.device.mainLanguage ? this.rest.device.mainLanguage : "es";
    const newChat =  this.cookieService.get('newChat');
    console.log('newChat', this.rest.device.chatBotId, this.rest.device.chatBotStandalone, newChat);
    if (this.rest.device.chatBotId && (this.rest.device.chatBotStandalone || newChat === 'true')){
      this.chatId = this.rest.device.chatBotId;
      this.chatService.chatId.set(this.chatId);
      return;
    }
   this.chatService.loadChat(mainLang);
  }
  
  public isCustomRoute(): boolean {
    return this.tools.isRoutePage('/custom-route');
  }
  
  public isRegsiterUserRoute(): boolean {
    return this.tools.isRoutePage('/register-user');
  }
}
